import React, { createContext, useContext, useState } from 'react';
import apiService from '../services/api.service';
// import jaLocale from '../locales/ja.json';

/**
 * Tenant Context
 * Manages tenant-specific configuration and localization content
 */

const TenantContext = createContext(null);

export const TenantProvider = ({ children }) => {
  const [tenant, setTenant] = useState({
    // Core tenant data
    tenantid: null,
    tenanturlname: null,
    tenantname: null,
    tenantpromocode: null,
    tenantmaxcardcount: 5, // default
    
    // Language data
    languages: [],
    tenantlanguagecontent: {},
    languageid: null,
    languagecode: null,
    
    // Configuration data
    tenantmasterconfig: {},
    tenantcontentconfig: {},
    
    // Visual assets
    tenantbannerimage: null,
    cdnBase: null,
    
    // App config (kept for backward compatibility)
    theme: 'default',
    config: {
      apiUrl: process.env.REACT_APP_API_BASE_URL,
      features: {
        passkeyLogin: true,
        guestMode: true,
        emailVerification: true
      }
    }
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Load tenant by URL name
   * @param {string} tenanturlname - The tenant URL name from route param
   */
  const loadTenant = async (tenanturlname) => {
    if (!tenanturlname) return;
    
    setIsLoading(true);
    setError(null);
    try {
      // read language from localStorage key "l"
      let lang = null;
      try {
        const raw = localStorage.getItem('l');
        if (raw) {
          lang = JSON.parse(raw);
        }
      } catch (e) {
        console.error('Failed to parse language from localStorage:', e);
      }

      const params = new URLSearchParams();
      params.set('tenanturlname', tenanturlname);
      if (lang?.languageid) {
        params.set('languageid', String(lang.languageid));
      }

      const res = await apiService.get(`/api/tenants/tenantgetbyurlname?${params.toString()}`);

      // If API returned the default empty object (no valid tenant), treat as 404
      if (!res?.tenantid) {
        setError('Tenant not found');
        setIsLoading(false);
        return;
      }

      // Parse tenantlanguagecontent if it's a JSON string
      let languageContent = {};
      if (res?.tenantlanguagecontent) {
        try {
          languageContent = typeof res.tenantlanguagecontent === 'string' 
            ? JSON.parse(res.tenantlanguagecontent)
            : res.tenantlanguagecontent;
        } catch (e) {
          console.error('Failed to parse tenantlanguagecontent:', e);
          languageContent = {};
        }
      }

      // Parse tenantmasterconfig if it's a JSON string
      let masterConfig = {};
      if (res?.tenantmasterconfig) {
        try {
          masterConfig = typeof res.tenantmasterconfig === 'string' 
            ? JSON.parse(res.tenantmasterconfig)
            : res.tenantmasterconfig;
        } catch (e) {
          console.error('Failed to parse tenantmasterconfig:', e);
          masterConfig = {};
        }
      }

      // Parse tenantcontentconfig if it's a JSON string
      let contentConfig = {};
      if (res?.tenantcontentconfig) {
        try {
          contentConfig = typeof res.tenantcontentconfig === 'string' 
            ? JSON.parse(res.tenantcontentconfig)
            : res.tenantcontentconfig;
        } catch (e) {
          console.error('Failed to parse tenantcontentconfig:', e);
          contentConfig = {};
        }
      }

      // Construct CDN base URL from tenanturlname
      const tenantUrlName = res?.tenanturlname || tenanturlname;
      const cdnBase = tenantUrlName ? `https://${tenantUrlName}-cdn.pulseid.com` : null;

      setTenant(prev => ({
        ...prev,
        // Core tenant data
        tenantid: res?.tenantid ?? prev.tenantid,
        tenanturlname: tenantUrlName,
        tenantname: res?.tenantname ?? prev.tenantname,
        tenantpromocode: res?.tenantpromocode ?? prev.tenantpromocode,
        tenantmaxcardcount: res?.tenantmaxcardcount ?? prev.tenantmaxcardcount,
        
        // Language data
        languages: Array.isArray(res?.languages) ? res.languages : prev.languages,
        tenantlanguagecontent: languageContent,
        languageid: res?.languageid ?? prev.languageid,
        languagecode: res?.languagecode ?? prev.languagecode,
        
        // Configuration data
        tenantmasterconfig: masterConfig,
        tenantcontentconfig: contentConfig,
        
        // Content fields
        cookiepolicy: res?.cookiepolicy ?? prev.cookiepolicy,
        tenantfaq: res?.tenantfaq ?? prev.tenantfaq,
        tenantfaqdisplay: res?.tenantfaqdisplay ?? prev.tenantfaqdisplay,
        tenanttermsandconditions: res?.tenanttermsandconditions ?? prev.tenanttermsandconditions,
        termsandconditionsdisplay: res?.termsandconditionsdisplay ?? prev.termsandconditionsdisplay,
        tenantservicepolicy: res?.tenantservicepolicy ?? prev.tenantservicepolicy,
        tenantservicepolicydisplay: res?.tenantservicepolicydisplay ?? prev.tenantservicepolicydisplay,
        
        // Visual assets
        tenantbannerimage: res?.tenantbannerimage ?? prev.tenantbannerimage,
        tenantlogo: res?.tenantlogo ?? prev.tenantlogo,
        cdnBase: cdnBase ?? prev.cdnBase,
        
        // Store original config property from initial state
        config: prev.config,
      }));

      // Persist default language from tenant into localStorage 'l'
      try {
        const langId = res?.languageid;
        const langCode = res?.languagecode;
        if (langId && langCode) {
          const current = localStorage.getItem('l');
          let shouldUpdate = true;
          if (current) {
            try {
              const parsed = JSON.parse(current);
              shouldUpdate = !(parsed?.languageid === langId && parsed?.languagecode === langCode);
            } catch {}
          }
          if (shouldUpdate) {
            localStorage.setItem('l', JSON.stringify({ languageid: langId, languagecode: langCode }));
          }
        }
      } catch (e) {
        console.warn('Failed to persist default language to localStorage', e);
      }
    } catch (e) {
      console.error('Failed to load tenant:', e);
      setError(e?.message || 'Failed to load tenant');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Switch locale
   */
  const switchLocale = async (language, tenanturlname) => {
    try {
      localStorage.setItem('l', JSON.stringify(language));
    } catch {}
    // reload tenant to get new language content
    if (tenanturlname) {
      await loadTenant(tenanturlname);
    }
  };

  /**
   * Update tenant configuration
   */
  const updateConfig = (config) => {
    setTenant(prev => ({
      ...prev,
      config: { ...prev.config, ...config }
    }));
  };

  /**
   * Get translated text by key path
   * Example: t('signup.title') or t('signup.errors.emailRequired')
   */
  const t = (keyPath) => {
    const keys = keyPath.split('.');
    let value = tenant.tenantlanguagecontent;
    
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        console.warn(`Translation key not found: ${keyPath}`);
        return keyPath; // Return key path if translation not found
      }
    }
    
    return value;
  };

  // No auto-load on mount; MainLayout will call loadTenant with route param

  const value = {
    tenant,
    isLoading,
    error,
    loadTenant,
    switchLocale,
    updateConfig,
    t
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

/**
 * Custom hook to use tenant context
 */
export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export default TenantContext;
